import { Button, Modal, RadioGroup } from "@design";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import type { MicroAppSourceModeOption } from "@/api";
import { Text } from "@/components/Text";

const MODES = ["caller", "author", "snapshot"] as const;
// The page's own modes are the model's, so each source starts on the least shared one it supports.
const SAFEST_FIRST = ["caller", "snapshot", "author"];

/**
 * Whose access serves each live source of a page being published or republished (the old publish dialog's
 * choice). Sends every binding's chosen mode, so the app gets exactly what the dialog showed.
 */
export function PublishDataAccess({
  sources,
  confirmLabel,
  onCancel,
  onPublish,
}: Readonly<{
  sources: MicroAppSourceModeOption[] | null;
  confirmLabel: string;
  onCancel: () => void;
  onPublish: (overrides: Record<string, string>) => void;
}>) {
  const { t } = useTranslation("buildChat", { keyPrefix: "microApp.access" });
  const [modes, setModes] = useState<Record<string, string>>({});
  const listed = sources ?? [];
  const chosen = (source: MicroAppSourceModeOption) =>
    modes[source.provider] ?? SAFEST_FIRST.find((mode) => source.supportedModes.includes(mode)) ?? source.currentMode;
  const overrides = () =>
    Object.fromEntries(listed.flatMap((source) => source.bindingKeys.map((key) => [key, chosen(source)])));
  return (
    <Modal
      open={sources !== null}
      onClose={onCancel}
      title={t("title")}
      actions={
        <>
          <Button variant="secondary" onClick={onCancel}>
            {t("cancel")}
          </Button>
          <Button variant="primary" onClick={() => onPublish(overrides())}>
            {confirmLabel}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <Text variant="body-sm" tone="secondary">
          {t("intro")}
        </Text>
        {listed.map((source) => (
          <div key={source.provider} className="flex flex-col gap-2">
            <Text variant="body-sm" weight="semibold">
              {source.label}
            </Text>
            <RadioGroup
              aria-label={source.label}
              value={chosen(source)}
              onChange={(mode) => setModes((prev) => ({ ...prev, [source.provider]: mode }))}
              options={MODES.filter((mode) => source.supportedModes.includes(mode)).map((mode) => ({
                value: mode,
                label: t(`mode.${mode}`),
                description: t(`consequence.${mode}`, { source: source.label }),
              }))}
            />
          </div>
        ))}
        {listed.some((source) => chosen(source) === "author") && (
          <div className="rounded-lg bg-warning-subtle p-3">
            <Text variant="caption" tone="warning">
              {t("shared")}
            </Text>
          </div>
        )}
      </div>
    </Modal>
  );
}
